"use client"

import * as React from "react"
import Link from "next/link"

import { Button } from "@/components/ui/button"
import { HowArenaV2WorksDialog } from "@/components/arena/v2/how-it-works"
import {
  ARENA_BRANCH,
  ARENA_EYEBROW,
  ARENA_SURFACE,
  ARENA_TEXT_ACTION,
} from "@/components/arena/v2/primitives"
import { cn } from "@/lib/utils"
import {
  CANCEL_WINDOW_MS,
  PICK_COUNT_RULES,
  formatMultiple,
  formatUsd,
  type PickCount,
} from "@/lib/arena/v2-types"

/* =============================================================================
   Arena V2, the slip bridge.

   This is the only Arena surface a player meets BEFORE the mechanic binds them,
   so it has to do four jobs in the height of four lines:

   0. Say that Arena is automatic. Every lineup of 2 to 6 picks is in
      it, there is nothing to accept and no second entry fee. A ledger under the
      To Win figure otherwise reads as an offer being made.
   1. Name the group they are about to join (pick count, settlement day, size).
   2. Introduce the SECOND way to win. The first way, the perfect-lineup
      guarantee, is already on screen twelve pixels above as the To Win figure
      EntryFeeSelector renders, so this block never prints that dollar amount
      again. It names the relationship instead. Two identical dollar figures,
      stacked and differently labelled at the moment of commitment, read as
      double the upside; that is the single worst thing this block could do.
   3. Say what happens if the group does not fill, before the player is in it.

   Everything numeric here comes from the slip's own entry fee and multiplier,
   or from PICK_COUNT_RULES, which is the published rule table shipped as
   reference data for copy. Nothing is invented and no group is faked: before
   submission there is no group object, so no fill count, no roster and no
   population figure appears anywhere in this file.

   This module deliberately imports nothing from lib/arena/v2-mock or
   lib/arena/v2-store. Those are the /arena prototype's fixtures and its QA
   scenario switcher; the slip is a production surface and must not carry them,
   nor let a QA toggle change what a player sees. The feature switch itself is
   `isArenaV2Enabled` in lib/config.
   ============================================================================= */

/**
 * What the block shows when it is rendered with no slip around it, which is how
 * the /arena prototype previews this surface. It is the same five-pick, $5.00
 * lineup the explainer works through, priced off the published rule table, so
 * the preview and the document it opens agree.
 */
const PREVIEW_PICK_COUNT: PickCount = 5
const PREVIEW_ENTRY_FEE_USD = 5

/** Arena runs on lineups of 2 to 6 picks. */
export function isArenaPickCount(pickCount: number): pickCount is PickCount {
  return pickCount >= 2 && pickCount <= 6 && Number.isInteger(pickCount)
}

/* -------------------------------------------------------------------------- */
/* ArenaEntryPreview                                                           */
/* -------------------------------------------------------------------------- */

interface ArenaEntryPreviewProps extends React.ComponentProps<"div"> {
  /** Live picks in the slip. */
  pickCount?: number
  /** What the player will actually pay, after any promo coupon. */
  entryFeeUsd?: number
  /** The multiple the slip is quoting, boost included. */
  quotedMultiplier?: number
  /** False for Ease Play, Yes/No and promo lineups. Renders the honest one-liner. */
  eligible?: boolean
  /** Why this lineup is out. Required when `eligible` is false. */
  ineligibleReason?: string
  /** True when the lineup holds at least one ICE pick, which cuts both ways in Arena. */
  hasIcePicks?: boolean
}

/**
 * The Arena block in the slip. Sits directly under the To Win figure and is the
 * last thing read before committing.
 *
 * The slip passes its own live numbers for everything. The props stay optional
 * so the /arena prototype can render this surface with nothing mounted around
 * it, in which case it falls back to the preview lineup declared at the top of
 * this file and to PICK_COUNT_RULES. There is no other source: no fixture, no
 * scenario, no store.
 */
export function ArenaEntryPreview({
  pickCount: pickCountProp,
  entryFeeUsd: entryFeeUsdProp,
  quotedMultiplier: quotedMultiplierProp,
  eligible: eligibleProp,
  ineligibleReason,
  hasIcePicks: hasIcePicksProp,
  className,
  ...props
}: ArenaEntryPreviewProps) {
  const [explainerOpen, setExplainerOpen] = React.useState(false)

  const pickCount = pickCountProp ?? PREVIEW_PICK_COUNT
  const entryFeeUsd = entryFeeUsdProp ?? PREVIEW_ENTRY_FEE_USD
  const quotedMultiplier =
    quotedMultiplierProp ??
    (isArenaPickCount(pickCount) ? PICK_COUNT_RULES[pickCount].maxGuarantee : 1)
  const eligible = eligibleProp ?? isArenaPickCount(pickCount)
  const hasIcePicks = hasIcePicksProp ?? false

  // Whole dollars drop their cents: "$60" is a prize, "$60.00" is a line item.
  const asPrize = (amount: number) =>
    amount % 1 === 0 ? `$${amount.toLocaleString("en-US")}` : formatUsd(amount)

  const eyebrowLabel = "text-[10px] font-semibold uppercase tracking-[0.14em]"
  const figure =
    "block font-open-sauce text-[17px] font-semibold leading-none tabular-nums tracking-tight text-white"
  const figureMultiple =
    "font-open-sauce text-[15px] font-semibold leading-none tabular-nums tracking-tight"

  if (!eligible || !isArenaPickCount(pickCount)) {
    // Not in Arena, but the payout still wears the Arena card language: the
    // same bordered field and pink wash, single-sided because there is only
    // one way to win. This replaces the Multiplier / To Win rows the
    // EntryFeeSelector used to print for these lineups. With no real quote
    // yet ($0 entry or no multiplier), a "$0 0x" card is noise, so only the
    // eligibility line renders until the numbers mean something.
    const hasQuote = entryFeeUsd > 0 && quotedMultiplier > 0
    if (!hasQuote) {
      return (
        <div className={cn("border-t border-grey-800 pt-3", className)} {...props}>
          <p className="text-[11px] leading-relaxed text-grey-300">
            {ineligibleReason ?? "Arena runs on lineups of 2 to 6 picks."}
          </p>
        </div>
      )
    }
    return (
      <div className={cn("border-t border-grey-800 pt-3", className)} {...props}>
        <div className="relative overflow-hidden rounded-lg border border-grey-700">
          <span
            aria-hidden
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(100deg, hsl(var(--primary-0) / 0.18) 0%, hsl(var(--primary-0) / 0.04) 100%)",
            }}
          />
          <div className="relative flex flex-col items-start gap-1 px-3 py-2">
            <span className={cn(eyebrowLabel, ARENA_BRANCH.perfect.tint)}>
              To win
            </span>
            <span className="flex items-baseline gap-1.5">
              <span className={cn(figure, "text-[23.5px]")}>
                {asPrize(entryFeeUsd * quotedMultiplier)}
              </span>
              <span className={cn(figureMultiple, ARENA_BRANCH.perfect.tint)}>
                {formatMultiple(quotedMultiplier)}
              </span>
            </span>
          </div>
        </div>
        <p className="mt-1.5 text-[11px] leading-relaxed text-grey-300">
          {ineligibleReason ?? "Arena runs on lineups of 2 to 6 picks."}
        </p>
      </div>
    )
  }

  const rule = PICK_COUNT_RULES[pickCount]
  const topScorePrizeUsd = entryFeeUsd * rule.leaderboardMultiplier
  const perfectPrizeUsdRaw = entryFeeUsd * quotedMultiplier
  const usd = asPrize(topScorePrizeUsd)
  const mult = formatMultiple(quotedMultiplier)

  return (
    // The two prizes as slip ledger rows, continuing the Entry / Multiplier
    // list above instead of opening a card of their own. !mt-1.5 overrides the
    // parent's space-y-4 (which outranks a plain margin utility), closing the
    // section gap to the selector's own 6px row rhythm so the five rows read
    // as one list.
    <div
      role="group"
      aria-label={`Arena prizes: perfect lineup pays ${mult}, or top score pays ${usd}. Higher of the two.`}
      className={cn("!mt-1.5 flex flex-col gap-1.5", className)}
      {...props}
    >
      {/* Perfect lineup — the payout row. The exact To Win composition from
          the selector (USDC tag + display-scale figure), relabelled by the
          branch that pays it, so the column keeps one money voice. */}
      <div className="flex items-center justify-between gap-2 px-5 text-sm">
        <span className="shrink-0 font-medium text-grey-400">Perfect Lineup</span>
        <span className="flex items-baseline gap-1.5 text-[23.5px] font-bold tabular-nums tracking-[-0.02em] text-foreground">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-grey-500">
            USDC
          </span>
          {formatUsd(perfectPrizeUsdRaw)}
        </span>
      </div>

      {/* Top score — the second way to pay, one size down: a real figure, but
          never competing with the guarantee above it. */}
      <div className="flex items-center justify-between gap-2 px-5 text-sm">
        <span className="shrink-0 font-medium text-grey-400">Top score</span>
        <span className="text-[15px] font-bold tabular-nums tracking-[-0.02em] text-grey-200">
          {formatUsd(topScorePrizeUsd)}
        </span>
      </div>

      {/* The one sentence of rules the rows cannot carry, and the way into
          the rest of them. */}
      <div className="flex items-center justify-between gap-3 px-5">
        <span className="text-[10.5px] leading-relaxed text-grey-300 md:text-[9px]">
          Top score, even with a miss
        </span>
        {/* Phone-only after: overlay widens the tap target to ~44px without
            moving layout; desktop keeps the approved 9.5px face. */}
        <button
          type="button"
          onClick={() => setExplainerOpen(true)}
          className={cn(
            "shrink-0 relative",
            ARENA_TEXT_ACTION,
            "text-[11px] md:text-[9.5px] after:absolute after:-inset-x-3 after:-inset-y-3 md:after:hidden",
          )}
        >
          How it works
        </button>
      </div>

      {hasIcePicks && (
        <p className="px-5 text-[11px] leading-relaxed text-grey-300">
          ICE picks lower your Arena score and your payout.
        </p>
      )}

      {/* One explainer in the product. The centered dialog is the shared
          document the group board opens too, so the slip cannot drift from it. */}
      <HowArenaV2WorksDialog
        open={explainerOpen}
        onOpenChange={setExplainerOpen}
        pickCount={pickCount}
        entryFeeUsd={entryFeeUsd}
      />
    </div>
  )
}


/* -------------------------------------------------------------------------- */
/* ArenaCancelWindowNotice                                                     */
/* -------------------------------------------------------------------------- */

function formatCountdown(msRemaining: number): string {
  const total = Math.max(0, Math.ceil(msRemaining / 1000))
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${minutes}:${seconds.toString().padStart(2, "0")}`
}

interface ArenaCancelWindowNoticeProps extends React.ComponentProps<"div"> {
  /** Epoch ms the lineup was submitted. */
  submittedAt: number
  /** What comes back if they cancel. */
  entryFeeUsd: number
  /** Called once the window closes, so the caller can drop the notice. */
  onExpire?: () => void
}

/**
 * The five-minute cancel window, stated where the player actually is at the
 * moment it opens: the slip they just submitted from, which is now empty.
 * Without this the window is invisible until they happen to open the entry.
 * The cancel itself stays where it already lives, on the entry, so there is one
 * cancel affordance in the product and this is a signpost to it.
 */
export function ArenaCancelWindowNotice({
  submittedAt,
  entryFeeUsd,
  onExpire,
  className,
  ...props
}: ArenaCancelWindowNoticeProps) {
  const [now, setNow] = React.useState(() => Date.now())

  React.useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [])

  const remaining = submittedAt + CANCEL_WINDOW_MS - now
  const expired = remaining <= 0

  React.useEffect(() => {
    if (expired) onExpire?.()
  }, [expired, onExpire])

  if (expired) return null

  return (
    <div
      className={cn(ARENA_SURFACE.card, className)}
      {...props}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className={cn(ARENA_EYEBROW, ARENA_BRANCH.perfect.tint)}>
          Arena
        </span>
        {/* The one figure on this card that decides anything: how long the
            money is still cancellable for. It gets the size to match. */}
        <span className="text-[17px] font-semibold tabular-nums tracking-tight text-white">
          {formatCountdown(remaining)} left
        </span>
      </div>
      <p className="mt-2 font-open-sauce text-[13px] font-semibold tracking-tight text-white">
        Waiting for a group
      </p>
      <p className="mt-1 text-[13px] leading-relaxed text-grey-300">
        Cancel for the full {formatUsd(entryFeeUsd)} back until the countdown ends.
      </p>
      <Button asChild variant="secondary" size="sm" className="mt-3 w-full">
        <Link href="/entries">Go to your entry</Link>
      </Button>
    </div>
  )
}
